import { useQuery } from '@tanstack/react-query';
import { extractAttributesList } from '@/utils/api-client';
import { evalService, type EvalRun } from '@/services/eval-service';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Empty,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  Badge,
} from 'prosci-ui';
import { format } from 'date-fns';

export function BenchmarksTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['evalRuns', 'completed'],
    queryFn: () => evalService.listRuns({ status: 'completed' }),
  });

  const runs: EvalRun[] =
    extractAttributesList(data);

  if (isLoading) {
    return <Empty><Spinner /></Empty>;
  }

  if (runs.length === 0) {
    return (
      <Empty>
        <EmptyTitle>No completed runs</EmptyTitle>
        <EmptyDescription>
          Complete an eval run to see benchmark data here.
        </EmptyDescription>
      </Empty>
    );
  }

  const latestScore = runs[0]?.overall_score;
  const previousScore = runs.length > 1 ? runs[1]?.overall_score : null;
  const scoreDelta = latestScore != null && previousScore != null
    ? latestScore - previousScore
    : null;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">
              {latestScore != null ? `${(latestScore * 100).toFixed(0)}%` : 'N/A'}
            </div>
            <div className="text-sm text-muted-foreground">Latest Score</div>
            {scoreDelta != null && (
              <div className={`text-xs mt-1 ${scoreDelta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {scoreDelta >= 0 ? '+' : ''}{(scoreDelta * 100).toFixed(1)}% from previous
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{runs.length}</div>
            <div className="text-sm text-muted-foreground">Completed Runs</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">
              {runs[0]?.total_cases ?? 0}
            </div>
            <div className="text-sm text-muted-foreground">Cases in Latest Run</div>
          </CardContent>
        </Card>
      </div>

      {/* Score history table */}
      <Card>
        <CardHeader>
          <CardTitle>Score History</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Passed</TableHead>
                <TableHead>Failed</TableHead>
                <TableHead>Avg Latency</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="font-medium">{run.name}</TableCell>
                  <TableCell>
                    {run.completed_at
                      ? format(new Date(run.completed_at), 'MMM d, yyyy HH:mm')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        run.overall_score != null && run.overall_score >= 0.8
                          ? 'default'
                          : run.overall_score != null && run.overall_score >= 0.5
                            ? 'secondary'
                            : 'destructive'
                      }
                    >
                      {run.overall_score != null
                        ? `${(run.overall_score * 100).toFixed(0)}%`
                        : 'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-green-600">{run.passed_cases}</TableCell>
                  <TableCell className="text-red-600">{run.failed_cases}</TableCell>
                  <TableCell>
                    {run.summary_stats?.avg_latency_ms != null
                      ? `${run.summary_stats.avg_latency_ms}ms`
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
